import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import config from '../config';
import {
    Download,
    MapPin,
    FileText,
    Loader2,
    AlertCircle,
    Calendar,
    Tag
} from 'lucide-react';

export default function PaymentHistory() {
    const { selectedProperty } = useOutletContext();
    const navigate = useNavigate();

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const getDates = () => {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setMonth(today.getMonth() - 13);
        const formatDate = (date) => date.toISOString().split('T')[0];
        return {
            today: formatDate(today),
            pastDate: formatDate(pastDate)
        };
    };

    const fetchPayments = async () => {
        if (!selectedProperty?.nro_cliente) return;

        const token = sessionStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        setLoading(true);
        setError(null);
        const { today, pastDate } = getDates();

        const params = new URLSearchParams({
            startDate: pastDate,
            endDate: today,
            nro_cliente: selectedProperty.nro_cliente
        });

        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-payments?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                sessionStorage.clear();
                navigate('/login');
                return;
            }

            if (!response.ok) throw new Error(`Error ${response.status}`);

            const data = await response.json();
            // Ordenamos por fecha de más reciente a más antiguo
            const dataArray = (Array.isArray(data) ? data : []).sort((a, b) =>
                new Date(b.fecha_hora) - new Date(a.fecha_hora)
            );

            setPayments(dataArray);
        } catch (err) {
            setError("No se pudo cargar el historial de pagos.");
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async (pago) => {
        const token = sessionStorage.getItem('token');
        if (!token || !pago?.id) return;

        setDownloadingId(pago.id);
        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-payments/download?payment_id=${pago.id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error("No se pudo descargar");

            const dateObj = new Date(pago.fecha_hora);
            const day = dateObj.getDate();
            const month = dateObj.toLocaleDateString('es-CL', { month: 'long' }).toLowerCase();
            const year = dateObj.getFullYear();
            const fileName = `comprobante-pago-${day}-${month}-${year}-folio-${pago.nro_comprobante}.pdf`;

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
        } finally {
            setDownloadingId(null);
        }
    };

    useEffect(() => {
        fetchPayments();
    }, [selectedProperty]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
    };

    const formatDateStr = (dateStr) => {
        if (!dateStr || dateStr.startsWith('0001')) return "N/A";
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="max-w-6xl mx-auto w-full animate-in fade-in duration-500 pb-10">
            <div className="mb-8 lg:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl lg:text-4xl font-black text-slate-800 mb-2">Pagos Realizados</h1>
                    <p className="text-slate-500">Historial completo de tus transacciones confirmadas.</p>
                </div>
            </div>

            <div className="mb-8 bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center gap-4">
                <div className="p-3 bg-sky-100 text-sky-600 rounded-2xl w-fit">
                    <MapPin size={24} />
                </div>
                <div>
                    <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-0.5 ml-1">Servicio Activo</label>
                    <div className="text-slate-700 font-bold text-lg leading-tight">
                        #{selectedProperty?.nro_cliente} - {selectedProperty?.up_direccion}
                    </div>
                </div>
            </div>

            {loading && (
                <div className="flex flex-col items-center justify-center py-24 text-sky-600 no-print">
                    <Loader2 className="animate-spin mb-4" size={48} />
                    <p className="font-bold text-lg">Cargando historial...</p>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-100 p-8 rounded-3xl flex flex-col items-center text-center gap-4 text-red-700 no-print">
                    <AlertCircle size={40} className="opacity-50" />
                    <div>
                        <p className="font-bold text-lg">Ocurrió un error</p>
                        <p className="text-sm opacity-80">{error}</p>
                    </div>
                    <button onClick={fetchPayments} className="px-6 py-2 bg-red-100 hover:bg-red-200 rounded-xl font-bold transition-colors shadow-sm">
                        Reintentar
                    </button>
                </div>
            )}

            {!loading && !error && (
                <div className="space-y-4">
                    {payments.length === 0 ? (
                        <div className="text-center py-24 bg-white rounded-3xl border border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium">No se registran pagos en el sistema para este periodo.</p>
                        </div>
                    ) : (
                        payments.map((pago) => {
                            const isDownloading = downloadingId === pago.id;
                            const isNulo = pago.in_estado_pago === 'NULO';

                            return (
                                <div key={pago.id} className={`payment-card group bg-white rounded-2xl p-5 md:p-6 shadow-sm border transition-all flex flex-col xl:flex-row xl:items-center justify-between gap-6 ${isNulo ? 'border-red-100 opacity-75' : 'border-slate-100 hover:shadow-md hover:border-sky-100'}`}>
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-y-4 gap-x-6 items-center">

                                        {/* Información del Comprobante */}
                                        <div className="col-span-1 md:col-span-2 lg:col-span-4 flex items-center gap-4">
                                            <div className={`p-3 rounded-xl hidden md:flex ${isNulo ? 'bg-red-50 text-red-400' : 'bg-sky-50 text-sky-600'}`}>
                                                <FileText size={20} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Comprobante</p>
                                                <p className="text-sm font-bold text-slate-700 font-mono">#{pago.nro_comprobante}</p>
                                            </div>
                                        </div>

                                        {/* Fecha y Hora */}
                                        <div className="lg:col-span-3">
                                            <div className="flex items-center gap-2 text-slate-400 mb-1">
                                                <Calendar size={12} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Fecha y Hora</span>
                                            </div>
                                            <p className="text-sm font-bold text-slate-600">{formatDateStr(pago.fecha_hora)}</p>
                                        </div>

                                        {/* Estado y Servicio */}
                                        <div className="lg:col-span-3">
                                            <div className="flex items-center gap-2 text-slate-400 mb-1">
                                                <Tag size={12} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Servicio / Estado</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <p className="text-xs font-bold text-slate-600 uppercase">{pago.tipo_servicio}</p>
                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter border ${
                                                    isNulo
                                                        ? 'bg-red-50 text-red-600 border-red-100'
                                                        : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                }`}>
                                                    {pago.in_estado_pago}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Monto */}
                                        <div className="lg:col-span-2 text-right">
                                            <p className="text-[10px] font-black text-sky-500 uppercase tracking-widest mb-0.5">Monto Pagado</p>
                                            <p className={`text-xl font-black tracking-tighter ${isNulo ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                                {formatCurrency(pago.monto_pago)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Acciones */}
                                    <div className="flex items-center gap-3 justify-end border-t xl:border-t-0 pt-4 xl:pt-0 no-print">
                                        {!isNulo && (
                                            <button
                                                onClick={() => handleDownloadPDF(pago)}
                                                disabled={isDownloading}
                                                className="w-full xl:w-auto px-5 py-3 bg-sky-600 text-white rounded-xl font-bold text-xs transition-all hover:bg-sky-700 shadow-lg shadow-sky-100 flex items-center justify-center gap-2 disabled:opacity-70 active:scale-95"
                                            >
                                                {isDownloading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                                                Descargar PDF
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}
